import parse, { domToReact } from 'html-react-parser';
import styles from '../styles/main.module.scss';

function capitalizeFirstLetter(str) {
  if (!str) return str; // Check for empty string or null/undefined input
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function toLowerCaseString(str) {
  if (typeof str !== 'string') {
    throw new TypeError('The input must be a string.');
  }
  return str.toLowerCase();
}

function isObjectNotEmpty(obj) {
  if (obj === null || obj === undefined) {
    return false;
  }
  return (
    obj &&
    typeof obj === 'object' &&
    !Array.isArray(obj) &&
    Object.keys(obj).length > 0
  );
}

/**
 * Creates an object from URL search parameters.
 * @returns {Object} - An object with key-value pairs from the URL search parameters.
 */
const getSearchParamsObject = (url) => {
  const searchParams = new URLSearchParams(url);
  const paramsObject = {};

  searchParams.forEach((value, key) => {
    paramsObject[key] = value;
  });

  return paramsObject;
};

/**
 * Appends parameters from an object to a base URL.
 * @param {string} base - The base URL to append the query parameters to.
 * @param {Object} paramsObject - The object containing query parameters to append.
 * @returns {string} - The constructed URL with query parameters.
 */
const appendParamsToUrl = (base, paramsObject) => {
  let url = base;

  Object.keys(paramsObject).forEach((key) => {
    url = `${url}${url === base ? `?${key}=` : `&${key}=`}${paramsObject[key]}`;
  });

  return url;
};

const ctaTheme = (theme) => {
  switch (theme) {
    case 'True blue (solid)': {
      return styles.trueBlueSolid;
    }
    case 'True blue (outline)': {
      return styles.trueBlueOutline;
    }
    case 'Light blue (solid)': {
      return styles.lightBlueSolid;
    }
    case 'Light blue (outline)': {
      return styles.lightBlueOutline;
    }
    case 'Red (outline)': {
      return styles.redOutline;
    }
    default: {
      return styles.redSolid;
    }
  }
};

// Utility function to set target="_blank" for external links
const setExternalLinksTargetBlank = (content) => {
  if (!content) {
    return '';
  }
  return parse(content, {
    /* eslint-disable consistent-return */
    replace: (domNode) => {
      if (domNode.name === 'a') {
        const href = domNode.attribs?.href || '';
        if (!href.includes('lendingclub.com')) {
          /* eslint-disable no-param-reassign */
          domNode.attribs.target = '_blank';
        }
        return domToReact([domNode]);
      }
    },
  });
};

const getPixelValue = (value) =>
  typeof value === 'string' && value.includes('px')
    ? parseInt(value, 10)
    : value;

const formatImagePath = (imagePath) => {
  // Check if the image path starts with '//' indicating it's a server image
  if (typeof imagePath === 'string' && imagePath?.startsWith('//')) {
    return `https:${imagePath}`;
  }
  if (typeof imagePath === 'object' && imagePath?.src?.startsWith('//')) {
    return `https:${imagePath.src}`;
  }
  // If the image path starts with '/', return it as is (local path or already valid)
  if (typeof imagePath === 'string' && imagePath?.startsWith('/')) {
    return imagePath;
  }
  if (typeof imagePath === 'object' && imagePath?.src?.startsWith('/')) {
    return imagePath.src;
  }
  // If the image path doesn't start with '/', prepend '/' to make it a valid local path
  if (typeof imagePath === 'object' && imagePath?.src) {
    return `/${imagePath.src}`;
  }
  return `/${imagePath}`;
};

const isCDNImage = (imageSrc) => {
  let src = '';
  // Check if imageSrc is an object or a string
  if (typeof imageSrc === 'object' && imageSrc !== null && imageSrc.src) {
    src = imageSrc.src; // Use the 'src' property from the object
  } else if (typeof imageSrc === 'string') {
    src = imageSrc; // Use the string value directly
  }

  // Check if the src is a CDN URL (starts with 'http' or '//')
  if (src.length > 0) {
    return src.startsWith('http') || src.startsWith('//');
  }
  return false; // Default return if src is not a valid CDN URL
};

const getImageDetails = (src, defaultWidth, defaultHeight) => {
  let imageSrc = '';
  let imageWidth = defaultWidth;
  let imageHeight = defaultHeight;

  // Check if src is an object or a string
  if (typeof src === 'object' && src !== null) {
    // Extract src, width, and height from the object
    imageSrc = formatImagePath(src.src); // Assuming formatImagePath is also imported
    imageWidth = src.width || defaultWidth;
    imageHeight = src.height || defaultHeight;
  } else if (typeof src === 'string') {
    imageSrc = formatImagePath(src); // Format the path using the utility function
  }

  return { imageSrc, imageWidth, imageHeight };
};

export {
  capitalizeFirstLetter,
  toLowerCaseString,
  isObjectNotEmpty,
  getSearchParamsObject,
  appendParamsToUrl,
  ctaTheme,
  setExternalLinksTargetBlank,
  getPixelValue,
  formatImagePath,
  isCDNImage,
  getImageDetails,
};
